import { useState, useRef, useEffect, useMemo } from "react";
import { Globe, Users } from "lucide-react";

export const ORIENT = {
  care:       { fill: "#34d399", label: "Care" },
  utility:    { fill: "#60a5fa", label: "Utility" },
  creative:   { fill: "#a78bfa", label: "Creative" },
  extraction: { fill: "#f87171", label: "Extraction" },
};

const polar = (cx, cy, r, angle) => ({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });

/* ── Inject CSS keyframes once globally ── */
const STYLE_ID = "shared-cst-anim-v2";
if (typeof document !== "undefined") {
  // Always update — remove old versions if they exist
  const old1 = document.getElementById("shared-cst-anim-styles");
  const old2 = document.getElementById("shared-cst-anim-v2");
  if (old1) old1.remove();
  if (old2) old2.remove();
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes cst-supernova { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
    @keyframes cst-pulse { 0%,100% { box-shadow: 0 0 20px rgba(34,197,94,0.3); } 50% { box-shadow: 0 0 40px rgba(34,197,94,0.6), 0 0 80px rgba(34,197,94,0.15); } }
    @keyframes cst-step-pop { 0% { transform: scale(0) rotate(-30deg); opacity: 0; } 70% { transform: scale(1.15) rotate(5deg); } 100% { transform: scale(1) rotate(0deg); opacity: 1; } }
    @keyframes cst-step-breathe { 0%,100% { transform: scale(1); box-shadow: 0 0 12px rgba(34,197,94,0.15); } 50% { transform: scale(1.06); box-shadow: 0 0 22px rgba(34,197,94,0.3); } }
    @keyframes cst-dot-burst { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.5); opacity: 0.7; } 100% { transform: scale(1); opacity: 1; } }
    @keyframes cst-dot-breathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.35); } }
    @keyframes cst-ripple { 0% { transform: translate(-50%,-50%) scale(0.5); opacity: 0.6; } 100% { transform: translate(-50%,-50%) scale(3); opacity: 0; } }
    @keyframes cst-float { 0%,100% { transform: translate(0,0); } 33% { transform: translate(2px,-3px); } 66% { transform: translate(-2px,2px); } }
    @keyframes cst-line-draw { from { stroke-dashoffset: 600; } to { stroke-dashoffset: 0; } }
    @keyframes cst-ring-expand { 0% { r: 0; opacity: 0; } 100% { opacity: 1; } }
    @keyframes cst-shimmer { 0%,100% { opacity: 0.15; } 50% { opacity: 0.4; } }
    @keyframes cst-star-twinkle { 0%,100% { opacity: 0; } 50% { opacity: 1; } }
    @keyframes cst-orbit-rotate { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -40; } }
    .cst-dot-alive { animation: cst-dot-breathe 2.5s ease-in-out infinite, cst-float 5s ease-in-out infinite; }
    .cst-dot-alive:nth-child(2n) { animation: cst-dot-breathe 3s ease-in-out 0.4s infinite, cst-float 4s ease-in-out -1s infinite; }
    .cst-dot-alive:nth-child(3n) { animation: cst-dot-breathe 2s ease-in-out 0.8s infinite, cst-float 6s ease-in-out -2s infinite; }
    .cst-dot-alive:nth-child(5n) { animation: cst-dot-breathe 3.5s ease-in-out 1.2s infinite, cst-float 3.5s ease-in-out -0.5s infinite; }
    .cst-ripple-ring { position: absolute; width: 14px; height: 14px; border-radius: 50%; pointer-events: none; animation: cst-ripple 2.5s ease-out infinite; }
    .cst-ripple-ring:nth-child(2) { animation-delay: 0.8s; }
    .cst-ripple-ring:nth-child(3) { animation-delay: 1.6s; }
    @keyframes cst-card-enter { 0% { transform: scale(0.8) translateY(10px); opacity: 0; } 100% { transform: scale(1) translateY(0); opacity: 1; } }
    @keyframes cst-card-spotlight { 0%,30% { border-color: rgba(34,197,94,0.5); box-shadow: 0 0 20px rgba(34,197,94,0.12); opacity: 1; } 35%,95% { border-color: rgba(255,255,255,0.06); box-shadow: none; opacity: 0.7; } 100% { border-color: rgba(34,197,94,0.5); box-shadow: 0 0 20px rgba(34,197,94,0.12); opacity: 1; } }
    @keyframes cst-polygon-draw { from { stroke-dashoffset: 2000; } to { stroke-dashoffset: 0; } }
    @keyframes cst-polygon-flow { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -30; } }
  `;
  document.head.appendChild(style);
}

const SharedConstellationMap = ({ data, activeStep, setActiveStep, showInsightCards = true }) => {
  const [hovered, setHovered] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [phase, setPhase] = useState(0);

  const W = 900, H = 580;
  const cx = W / 2, cy = H / 2;
  const stepR = 180, appR = 48;
  const steps = data.steps;
  const n = steps.length;

  const pct = (px, total) => `${(px / total) * 100}%`;

  // Compute positions — apps in wide semi-circular arcs on outer side of each step
  const stepXY = [], appXY = [];
  for (let si = 0; si < n; si++) {
    const a = (si / n) * Math.PI * 2 - Math.PI / 2;
    stepXY.push({ ...polar(cx, cy, stepR, a), angle: a });
    const apps = steps[si].apps;
    const pos = [];
    for (let ai = 0; ai < apps.length; ai++) {
      const spread = Math.PI * 1.1;
      const sa = apps.length === 1 ? a : a - spread / 2 + (ai / (apps.length - 1)) * spread;
      pos.push(polar(stepXY[si].x, stepXY[si].y, appR, sa));
    }
    appXY.push(pos);
  }

  // Stable fingerprint for phase animation — doesn't change on parent re-renders
  const stepsFingerprint = useMemo(() => steps.map(s => s.name).join("|"), [steps]);

  // Staggered entrance
  useEffect(() => {
    setPhase(0);
    const t1 = setTimeout(() => setPhase(1), 50);
    const t2 = setTimeout(() => setPhase(2), 400);
    const t3 = setTimeout(() => setPhase(3), 700);
    const t4 = setTimeout(() => setPhase(4), 1100);
    const t5 = setTimeout(() => setPhase(5), 1600);
    return () => { [t1, t2, t3, t4, t5].forEach(clearTimeout); };
  }, [stepsFingerprint]);

  // Background stars
  const stars = useRef(Array.from({ length: 40 }, () => ({
    x: Math.random() * W, y: Math.random() * H,
    r: Math.random() * 1.5 + 0.5, delay: Math.random() * 5,
    dur: Math.random() * 3 + 2,
  }))).current;

  // Polygon path connecting all step nodes (visible orbit ring between steps)
  const polygonPath = useMemo(() => {
    if (n === 0) return "";
    return stepXY.map((s, i) => `${i === 0 ? "M" : "L"} ${s.x} ${s.y}`).join(" ") + " Z";
  }, [n, stepXY]);

  // Dynamic orbit keyframes — particle travels center → step 1 → 2 → ... → N → center
  // Uses PERCENTAGE positions to match responsive container scaling
  const orbitId = useRef(`scm-orbit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`).current;

  // Direct DOM injection for polygon dotted lines (bypasses React SVG rendering issues)
  useEffect(() => {
    if (phase < 2 || n < 3) return;
    const vizEl = document.querySelector('[data-testid="constellation-viz"] svg');
    if (!vizEl) return;
    // Remove old polygon lines
    vizEl.querySelectorAll('.scm-poly-edge').forEach(el => el.remove());
    vizEl.querySelectorAll('.scm-eco-line').forEach(el => el.remove());
    // Add polygon dotted edges between adjacent step nodes
    stepXY.forEach((s, i) => {
      const next = stepXY[(i + 1) % n];
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', s.x);
      line.setAttribute('y1', s.y);
      line.setAttribute('x2', next.x);
      line.setAttribute('y2', next.y);
      line.setAttribute('stroke', 'rgba(180,200,240,0.7)');
      line.setAttribute('stroke-width', '1.8');
      line.setAttribute('stroke-dasharray', '4 6');
      line.setAttribute('class', 'scm-poly-edge');
      vizEl.appendChild(line);
    });
    // Add ecosystem mesh lines between apps in adjacent steps
    if (phase >= 4) {
      steps.forEach((step, si) => {
        step.apps.forEach((app, ai) => {
          const nextSi = (si + 1) % n;
          steps[nextSi].apps.forEach((na, nai) => {
            if (app.value_orientation !== na.value_orientation) return;
            const from = appXY[si]?.[ai], to = appXY[nextSi]?.[nai];
            if (!from || !to) return;
            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', from.x);
            line.setAttribute('y1', from.y);
            line.setAttribute('x2', to.x);
            line.setAttribute('y2', to.y);
            const o = ORIENT[app.value_orientation] || ORIENT.utility;
            line.setAttribute('stroke', o.fill);
            line.setAttribute('stroke-width', '0.8');
            line.setAttribute('stroke-dasharray', '3 5');
            line.setAttribute('opacity', '0.3');
            line.setAttribute('class', 'scm-eco-line');
            line.style.animation = `cst-shimmer 3s ease-in-out ${(si * 0.5 + ai * 0.3) % 2}s infinite`;
            vizEl.appendChild(line);
          });
        });
      });
    }
    return () => {
      vizEl?.querySelectorAll('.scm-poly-edge, .scm-eco-line').forEach(el => el.remove());
    };
  }, [phase, n, stepXY, appXY, steps]);

  useEffect(() => {
    if (n === 0) return;
    let existing = document.getElementById(orbitId);
    if (existing) existing.remove();

    const pX = (x) => `${((x - 8) / W * 100).toFixed(2)}%`;
    const pY = (y) => `${((y - 8) / H * 100).toFixed(2)}%`;

    const totalStops = n + 2;
    const frames = [];
    frames.push(`0% { left: ${pX(cx)}; top: ${pY(cy)}; opacity: 1; box-shadow: 0 0 12px #22c55e, 0 0 24px #22c55e40; }`);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      const sx = cx + Math.cos(a) * stepR;
      const sy = cy + Math.sin(a) * stepR;
      const p = ((i + 1) / totalStops * 100).toFixed(1);
      frames.push(`${p}% { left: ${pX(sx)}; top: ${pY(sy)}; opacity: 1; box-shadow: 0 0 16px #22c55e, 0 0 30px #22c55e60; }`);
    }
    const retPct = ((n + 1) / totalStops * 100).toFixed(1);
    frames.push(`${retPct}% { left: ${pX(cx)}; top: ${pY(cy)}; opacity: 0.6; box-shadow: 0 0 8px #22c55e; }`);
    frames.push(`100% { left: ${pX(cx)}; top: ${pY(cy)}; opacity: 1; box-shadow: 0 0 12px #22c55e, 0 0 24px #22c55e40; }`);
    const s = document.createElement("style");
    s.id = orbitId;
    s.textContent = `@keyframes ${orbitId} { ${frames.join(" ")} }`;
    document.head.appendChild(s);
    return () => { document.getElementById(orbitId)?.remove(); };
  }, [n, cx, cy, stepR, orbitId, W, H]);

  return (
    <div className="w-full flex justify-center overflow-hidden" data-testid="constellation-viz">
      <div className="relative w-full" onClick={() => setSelectedApp(null)}
        style={{ maxWidth: W, aspectRatio: `${W}/${H}`, background: "radial-gradient(ellipse at center, #0c1222 0%, #060a12 100%)", borderRadius: 16, overflow: "hidden" }}>

        {/* SVG: stars + animated lines */}
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1 }}>
          {/* Twinkling stars */}
          {stars.map((s, i) => (
            <circle key={`star-${i}`} cx={s.x} cy={s.y} r={s.r} fill="white"
              style={{ animation: `cst-star-twinkle ${s.dur}s ease-in-out ${s.delay}s infinite` }} />
          ))}

          {/* Dashed orbit ring */}
          {phase >= 2 && (
            <circle cx={cx} cy={cy} r={stepR} fill="none" stroke="#22c55e" strokeWidth="1" strokeDasharray="6 10"
              style={{ opacity: 0.25 }} />
          )}

          {/* Center-to-step lines — animated draw-in */}
          {phase >= 2 && stepXY.map((s, i) => {
            const len = Math.sqrt((s.x - cx) ** 2 + (s.y - cy) ** 2);
            return (
              <line key={`cl-${i}`} x1={cx} y1={cy} x2={s.x} y2={s.y}
                stroke="#22c55e" strokeWidth="1.5" opacity="0.35"
                strokeDasharray={len} strokeDashoffset={len}
                style={{ animation: `cst-line-draw 0.6s ease-out ${0.1 * i}s forwards` }} />
            );
          })}

          {/* Step-to-app connector lines — animated draw-in */}
          {phase >= 4 && steps.map((step, si) =>
            appXY[si].map((ap, ai) => {
              const s = stepXY[si];
              const len = Math.sqrt((ap.x - s.x) ** 2 + (ap.y - s.y) ** 2);
              return (
                <line key={`al-${si}-${ai}`} x1={s.x} y1={s.y} x2={ap.x} y2={ap.y}
                  stroke="#475569" strokeWidth="1" opacity="0.25"
                  strokeDasharray={len} strokeDashoffset={len}
                  style={{ animation: `cst-line-draw 0.4s ease-out ${0.05 * ai}s forwards` }} />
              );
            })
          )}

        </svg>

        {/* IDEA node with orientation donut ring */}
        <div style={{
          position: "absolute", left: pct(cx - 34, W), top: pct(cy - 34, H), width: "7.5%", aspectRatio: "1", maxWidth: 68,
          borderRadius: "50%", zIndex: 10, pointerEvents: "none",
          animation: phase >= 1 ? "cst-supernova 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards, cst-pulse 3s ease-in-out 0.8s infinite" : "none",
          opacity: phase >= 1 ? 1 : 0,
        }}>
          {(() => {
            const all = steps.flatMap(s => s.apps);
            const total = all.length || 1;
            const counts = { care: 0, utility: 0, creative: 0, extraction: 0 };
            all.forEach(a => { if (counts[a.value_orientation] !== undefined) counts[a.value_orientation]++; });
            let angle = 0;
            const segments = Object.entries(counts).filter(([, c]) => c > 0).map(([k, c]) => {
              const start = angle;
              angle += (c / total) * 360;
              return `${ORIENT[k].fill} ${start}deg ${angle}deg`;
            });
            const grad = segments.length > 0 ? `conic-gradient(${segments.join(", ")})` : "#064e3b";
            return (
              <div style={{ width: 68, height: 68, borderRadius: "50%", background: grad, padding: 6, boxShadow: "0 0 30px rgba(34,197,94,0.3)" }}>
                <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: "#064e3b", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ color: "#86efac", fontSize: 11, fontWeight: 800, letterSpacing: 1.5 }}>IDEA</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Step nodes */}
        {stepXY.map((s, si) => {
          const active = activeStep === si;
          const visible = phase >= 3;
          return (
            <div key={`step-${si}`}>
              <div onClick={() => setActiveStep(active ? null : si)} data-testid={`step-node-${si}`}
                style={{
                  position: "absolute", left: pct(s.x - 22, W), top: pct(s.y - 22, H), width: "5%", aspectRatio: "1", maxWidth: 44, minWidth: 28,
                  borderRadius: "50%", background: active ? "#166534" : "#0f3d2e",
                  border: "3px solid #22c55e", cursor: "pointer", zIndex: 5,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: active ? "0 0 25px rgba(34,197,94,0.35)" : "0 0 12px rgba(34,197,94,0.1)",
                  animation: visible ? `cst-step-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) ${si * 0.08}s both, cst-step-breathe ${3 + si * 0.3}s ease-in-out ${si * 0.08 + 0.6}s infinite` : "none",
                  opacity: visible ? undefined : 0,
                  transition: "box-shadow 0.3s, background 0.3s",
                }}>
                <span style={{ color: "white", fontSize: 15, fontWeight: 800 }}>{si + 1}</span>
              </div>
              <div style={{
                position: "absolute", left: pct(s.x - 55, W), top: pct(s.y + 26, H), width: "12%", maxWidth: 110, zIndex: 8,
                textAlign: "center", fontSize: 10, fontWeight: 600, lineHeight: 1.2,
                color: active ? "#e2e8f0" : "#94a3b8", pointerEvents: "none",
                background: "rgba(6,10,18,0.85)", borderRadius: 4, padding: "2px 4px",
                animation: visible ? `cst-step-pop 0.5s ease-out ${si * 0.08 + 0.15}s both` : "none",
                opacity: visible ? undefined : 0,
              }}>
                {steps[si].name}
              </div>
            </div>
          );
        })}

        {/* Orbiting arrow particle — travels center → step 1 → 2 → ... → N → center */}
        {phase >= 5 && (
          <>
            {/* Main arrow particle */}
            <div data-testid="orbit-particle" style={{
              position: "absolute", width: 0, height: 0, zIndex: 8, pointerEvents: "none",
              borderLeft: "10px solid transparent", borderRight: "10px solid transparent",
              borderBottom: "22px solid #4ade80",
              filter: "drop-shadow(0 0 12px #22c55e) drop-shadow(0 0 24px #22c55eaa) drop-shadow(0 0 40px #22c55e50)",
              animation: `${orbitId} ${n * 1.2}s ease-in-out infinite`,
            }} />
            {/* Trailing glow 1 */}
            <div style={{
              position: "absolute", width: 16, height: 16, borderRadius: "50%",
              background: "#22c55e", zIndex: 7, pointerEvents: "none",
              animation: `${orbitId} ${n * 1.2}s ease-in-out -0.15s infinite`,
              filter: "blur(5px)",
              opacity: 0.7,
            }} />
            {/* Trailing glow 2 */}
            <div style={{
              position: "absolute", width: 12, height: 12, borderRadius: "50%",
              background: "#22c55e60", zIndex: 6, pointerEvents: "none",
              animation: `${orbitId} ${n * 1.2}s ease-in-out -0.3s infinite`,
              filter: "blur(8px)",
              opacity: 0.5,
            }} />
          </>
        )}

        {/* App dots */}
        {steps.map((step, si) =>
          step.apps.map((app, ai) => {
            const p = appXY[si][ai];
            const o = ORIENT[app.value_orientation] || ORIENT.utility;
            const isH = hovered?.si === si && hovered?.ai === ai;
            const isSel = selectedApp?.si === si && selectedApp?.ai === ai;
            const visible = phase >= 4;
            return (
              <div key={`app-${si}-${ai}`}>
                {visible && !isSel && (
                  <>
                    <div className="cst-ripple-ring" style={{ left: p.x, top: p.y, border: `1px solid ${o.fill}`, animationDelay: `${(si * 0.5 + ai * 0.3) % 2.5}s` }} />
                    <div className="cst-ripple-ring" style={{ left: p.x, top: p.y, border: `1px solid ${o.fill}`, animationDelay: `${((si * 0.5 + ai * 0.3) % 2.5) + 0.8}s` }} />
                  </>
                )}
                <div className={visible ? "cst-dot-alive" : ""}
                  onMouseEnter={() => { setHovered({ si, ai }); setActiveStep(si); }}
                  onMouseLeave={() => setHovered(null)}
                  onClick={(e) => { e.stopPropagation(); setSelectedApp(isSel ? null : { si, ai, app, pos: p }); }}
                  data-testid={`app-dot-${si}-${ai}`}
                  style={{
                    position: "absolute", left: pct(p.x - 7, W), top: pct(p.y - 7, H),
                    width: isH || isSel ? 20 : 14, height: isH || isSel ? 20 : 14,
                    borderRadius: app.entity_type === "device" ? "3px" : app.entity_type === "research" ? "2px" : "50%",
                    transform: app.entity_type === "research" ? "rotate(45deg)" : "none",
                    background: o.fill, border: isSel ? "2px solid white" : app.entity_type === "device" ? "1.5px solid rgba(96,165,250,0.5)" : app.entity_type === "research" ? "1.5px solid rgba(6,182,212,0.5)" : "1px solid rgba(255,255,255,0.3)",
                    cursor: "pointer", zIndex: isSel ? 15 : 3,
                    boxShadow: isSel ? `0 0 14px ${o.fill}` : isH ? `0 0 10px ${o.fill}80` : `0 0 6px ${o.fill}40`,
                    animationDelay: visible ? `${si * 0.06 + ai * 0.04}s` : undefined,
                    opacity: visible ? undefined : 0,
                  }} />
                {isH && !isSel && (
                  <div style={{
                    position: "absolute", left: pct(p.x - 70, W), top: pct(p.y - 36, H), width: 140, zIndex: 20,
                    background: "rgba(0,0,0,0.92)", border: `1px solid ${o.fill}`, borderRadius: 8,
                    padding: "4px 8px", textAlign: "center", fontSize: 10, fontWeight: 600, color: "#fff",
                    pointerEvents: "none", animation: "cst-supernova 0.15s ease-out",
                  }}>
                    {app.product_name}
                    <div style={{ fontSize: 8, color: o.fill }}>{app.entity_type === "device" ? "Hardware" : app.entity_type === "research" ? "Research" : "Software"} · {o.label} · {app.country || "Global"}</div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Expanded app detail card */}
        {selectedApp && (
          <div data-testid="app-detail-card" onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              left: Math.min(selectedApp.pos.x + 18, W - 230),
              top: Math.max(selectedApp.pos.y - 60, 8),
              width: 220, zIndex: 30,
              background: "rgba(10,15,25,0.96)", backdropFilter: "blur(12px)",
              border: `1px solid ${(ORIENT[selectedApp.app.value_orientation] || ORIENT.utility).fill}50`,
              borderRadius: 12, padding: "14px 16px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)",
              animation: "cst-supernova 0.2s ease-out",
            }}>
            <button onClick={() => setSelectedApp(null)} style={{ position: "absolute", top: 8, right: 10, background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>&times;</button>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", marginBottom: 4, paddingRight: 16 }}>{selectedApp.app.product_name}</div>
            {selectedApp.app.tagline && <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 8, lineHeight: 1.4 }}>{selectedApp.app.tagline}</div>}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              {(() => { const o = ORIENT[selectedApp.app.value_orientation] || ORIENT.utility; return (
                <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 8px", borderRadius: 99, border: `1px solid ${o.fill}44`, color: o.fill, background: `${o.fill}15` }}>{o.label}</span>
              ); })()}
              {selectedApp.app.country && (
                <span style={{ fontSize: 9, fontWeight: 500, padding: "2px 8px", borderRadius: 99, border: "1px solid rgba(255,255,255,0.1)", color: "#94a3b8", display: "flex", alignItems: "center", gap: 3 }}>
                  <Globe style={{ width: 10, height: 10 }} />{selectedApp.app.country}
                </span>
              )}
              {selectedApp.app.founder_composition && selectedApp.app.founder_composition !== "unknown" && (
                <span style={{ fontSize: 9, fontWeight: 500, padding: "2px 8px", borderRadius: 99, border: "1px solid rgba(255,255,255,0.1)", color: "#94a3b8", display: "flex", alignItems: "center", gap: 3 }}>
                  <Users style={{ width: 10, height: 10 }} />{selectedApp.app.founder_composition.replace("_", " ")}
                </span>
              )}
            </div>
            {selectedApp.app.website_url && (
              <a href={selectedApp.app.website_url} target="_blank" rel="noopener noreferrer" data-testid="app-detail-link"
                style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, color: "#22c55e", textDecoration: "none" }}>
                Visit website &rarr;
              </a>
            )}
          </div>
        )}

        {/* Orbiting Insight Cards (optional) */}
        {showInsightCards && phase >= 5 && (() => {
          const allApps = steps.flatMap(s => s.apps);
          const total = allApps.length || 1;
          const countries = new Set(allApps.map(a => a.country).filter(Boolean));
          const femaleCount = allApps.filter(a => a.founder_composition === "female_only").length;
          const mixedCount = allApps.filter(a => a.founder_composition === "mixed").length;
          const diversityPct = Math.round((femaleCount + mixedCount) / total * 100);
          const o = ORIENT[data.idea_orientation] || ORIENT.utility;
          const cardStyle = (pos, delay) => ({
            position: "absolute", ...pos, width: "min(210px, 45%)", zIndex: 12,
            background: "rgba(8,14,26,0.92)", backdropFilter: "blur(14px)",
            borderRadius: 12, padding: "12px 14px", pointerEvents: "none",
            border: "1px solid rgba(255,255,255,0.08)",
            animation: `cst-card-enter 0.5s ease-out ${delay}s both, cst-card-spotlight 6s ease-in-out ${delay}s infinite`,
          });
          return (
            <>
              <div style={cardStyle({ left: 8, top: 8 }, 1.8)} data-testid="orbit-card-orientation">
                <p style={{ fontSize: 8, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>
                  {data.idea_orientation ? "Your Idea Is" : "Convergence Map"}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: o.fill, boxShadow: `0 0 8px ${o.fill}` }} />
                  <span style={{ fontSize: 15, fontWeight: 800, color: o.fill }}>{o.label}</span>
                </div>
                <p style={{ fontSize: 10, color: "#cbd5e1", lineHeight: 1.5 }}>{data.idea_orientation_reason || "Software + Hardware convergence"}</p>
              </div>

              <div style={cardStyle({ right: 8, top: 8 }, 2.2)} data-testid="orbit-card-dna">
                <p style={{ fontSize: 8, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 }}>Ecosystem DNA</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "5px 10px", fontSize: 11 }}>
                  <span style={{ color: "#cbd5e1" }}>Entities</span><span style={{ fontWeight: 700, color: "#34d399", textAlign: "right" }}>{data.total_apps_matched || total}</span>
                  <span style={{ color: "#cbd5e1" }}>Countries</span><span style={{ fontWeight: 700, color: "#22d3ee", textAlign: "right" }}>{countries.size}</span>
                  <span style={{ color: "#cbd5e1" }}>Diversity</span><span style={{ fontWeight: 700, color: "#a78bfa", textAlign: "right" }}>{diversityPct}%</span>
                  <span style={{ color: "#cbd5e1" }}>Steps</span><span style={{ fontWeight: 700, color: "#e2e8f0", textAlign: "right" }}>{steps.length}</span>
                </div>
              </div>

              {data.similar_apps && (
                <div style={cardStyle({ left: 8, bottom: 8 }, 2.6)} data-testid="orbit-card-similar">
                  <p style={{ fontSize: 8, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 }}>Similar Apps</p>
                  {data.similar_apps.length > 0 ? data.similar_apps.slice(0, 3).map((app, i) => {
                    const ao = ORIENT[app.value_orientation] || ORIENT.utility;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: ao.fill, flexShrink: 0 }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{app.product_name}</span>
                      </div>
                    );
                  }) : (
                    <p style={{ fontSize: 10, color: "#22c55e", fontWeight: 600 }}>Unique idea, no close matches</p>
                  )}
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default SharedConstellationMap;
